import {
  STAT_CATEGORIES,
  HITTING_STATS,
  PITCHING_STATS,
  FIELDING_STATS,
  getPlayerDataFieldName,
} from "../constants/stats";

const LOWER_IS_BETTER = new Set(["era", "e", FIELDING_STATS.ERRORS]);

export const COMPARE_CAREER_STAT_ROWS = {
  [STAT_CATEGORIES.HITTING]: [
    { key: "years", label: "Seasons", format: "integer" },
    { key: "games", label: "G", format: "integer" },
    { key: "atBat", label: "AB", format: "integer" },
    { key: "hits", label: "H", format: "integer", statName: HITTING_STATS.HITS },
    {
      key: "homeruns",
      label: "HR",
      format: "integer",
      statName: HITTING_STATS.HOME_RUNS,
    },
    {
      key: "runBattedIn",
      label: "RBI",
      format: "integer",
      statName: HITTING_STATS.RBI,
    },
    {
      key: "battingAverage",
      label: "AVG",
      format: "average",
      statName: HITTING_STATS.BATTING_AVERAGE,
    },
    {
      key: "onBasePercentage",
      label: "OBP",
      format: "average",
      statName: HITTING_STATS.ON_BASE_PERCENTAGE,
    },
    {
      key: "sluggingAverage",
      label: "SLG",
      format: "average",
      statName: HITTING_STATS.SLUGGING_AVERAGE,
    },
    { key: "ops", label: "OPS", format: "average", computed: true },
    { key: "runs", label: "R", format: "integer", statName: HITTING_STATS.RUNS },
    {
      key: "doubles",
      label: "2B",
      format: "integer",
      statName: HITTING_STATS.DOUBLES,
    },
    {
      key: "triples",
      label: "3B",
      format: "integer",
      statName: HITTING_STATS.TRIPLES,
    },
    {
      key: "baseOnBalls",
      label: "BB",
      format: "integer",
      statName: HITTING_STATS.WALKS,
    },
    {
      key: "stolenBases",
      label: "SB",
      format: "integer",
      statName: HITTING_STATS.STOLEN_BASES,
    },
    {
      key: "strikeOut",
      label: "SO",
      format: "integer",
      statName: HITTING_STATS.STRIKEOUTS,
    },
  ],
  [STAT_CATEGORIES.PITCHING]: [
    { key: "years", label: "Seasons", format: "integer" },
    { key: "games", label: "G", format: "integer" },
    { key: "wins", label: "W", format: "integer", statName: PITCHING_STATS.WINS },
    {
      key: "losses",
      label: "L",
      format: "integer",
      statName: PITCHING_STATS.LOSSES,
    },
    { key: "era", label: "ERA", format: "era", statName: PITCHING_STATS.ERA },
    {
      key: "strikeOuts",
      label: "SO",
      format: "integer",
      statName: PITCHING_STATS.STRIKEOUTS,
    },
    {
      key: "baseOnBalls",
      label: "BB",
      format: "integer",
      statName: PITCHING_STATS.WALKS,
    },
    {
      key: "hits",
      label: "H",
      format: "integer",
      statName: PITCHING_STATS.HITS_ALLOWED,
    },
    {
      key: "earnedRuns",
      label: "ER",
      format: "integer",
      statName: PITCHING_STATS.EARNED_RUNS,
    },
    {
      key: "completeGames",
      label: "CG",
      format: "integer",
      statName: PITCHING_STATS.COMPLETE_GAMES,
    },
    {
      key: "shutouts",
      label: "SHO",
      format: "integer",
      statName: PITCHING_STATS.SHUTOUTS,
    },
    {
      key: "saves",
      label: "SV",
      format: "integer",
      statName: PITCHING_STATS.SAVES,
    },
    {
      key: "inningsPitched",
      label: "IP",
      format: "decimal",
      statName: PITCHING_STATS.INNINGS_PITCHED,
    },
    {
      key: "homeRuns",
      label: "HR",
      format: "integer",
      statName: PITCHING_STATS.HOME_RUNS_ALLOWED,
    },
  ],
  [STAT_CATEGORIES.FIELDING]: [
    { key: "years", label: "Seasons", format: "integer" },
    { key: "g", label: "G", format: "integer", statName: FIELDING_STATS.GAMES },
    { key: "ch", label: "Ch", format: "integer", statName: FIELDING_STATS.CHANCES },
    { key: "po", label: "PO", format: "integer", statName: FIELDING_STATS.PUTOUTS },
    { key: "a", label: "A", format: "integer", statName: FIELDING_STATS.ASSISTS },
    { key: "e", label: "E", format: "integer", statName: FIELDING_STATS.ERRORS },
    {
      key: "FLD%",
      label: "FLD%",
      format: "average",
      statName: FIELDING_STATS.FIELDING_PERCENTAGE,
    },
    {
      key: "rf",
      label: "RF",
      format: "decimal",
      statName: FIELDING_STATS.RANGE_FACTOR,
    },
  ],
};

const parseNumeric = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = parseFloat(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

export const getCareerStatRawValue = (career, row) => {
  if (!career) return null;

  if (row.computed && row.key === "ops") {
    const obp = parseNumeric(career.onBasePercentage);
    const slg = parseNumeric(career.sluggingAverage);
    if (obp === null || slg === null) return null;
    return obp + slg;
  }

  return parseNumeric(career[row.key]);
};

export const formatCareerStatValue = (value, format) => {
  if (value === null || value === undefined) return "—";

  switch (format) {
    case "average":
      return parseFloat(value).toFixed(3);
    case "era":
      return parseFloat(value).toFixed(2);
    case "decimal":
      return parseFloat(value).toFixed(1);
    case "integer":
    default:
      return Math.round(value).toLocaleString();
  }
};

export const getCompareCareerRows = (statCategory) =>
  COMPARE_CAREER_STAT_ROWS[statCategory] || COMPARE_CAREER_STAT_ROWS.hitting;

export const isSelectedStatRow = (row, selectedStat) => {
  if (!selectedStat) return false;
  if (row.statName === selectedStat) return true;

  const mappedField = getPlayerDataFieldName(selectedStat);
  return row.key === mappedField;
};

export const getNumericLeaderIndex = (
  values,
  { lowerIsBetter = false } = {}
) => {
  const numericValues = values.map((value) => parseNumeric(value));
  const valid = numericValues.filter((value) => value !== null);
  if (valid.length < 2) return -1;

  const target = lowerIsBetter ? Math.min(...valid) : Math.max(...valid);
  const leaders = numericValues
    .map((value, index) => ({ value, index }))
    .filter(({ value }) => value !== null && value === target);

  return leaders.length === 1 ? leaders[0].index : -1;
};

export const getRowLeaderIndex = (values, rowKey) =>
  getNumericLeaderIndex(values, {
    lowerIsBetter: LOWER_IS_BETTER.has(rowKey),
  });

export const hasCareerStatsForCategory = (playerData, statCategory) => {
  const career = playerData?.stats?.[statCategory]?.career;
  return Boolean(career && Object.keys(career).length > 0);
};

const extractYear = (dateStr) => {
  if (!dateStr) return null;
  const match = String(dateStr).match(/\d{4}/);
  return match ? match[0] : null;
};

export const getPlayerCompareSubtitle = (playerData, statCategory) => {
  const bio = playerData?.bio;
  const career = playerData?.stats?.[statCategory]?.career;
  const parts = [];

  if (bio?.position) {
    parts.push(bio.position);
  } else if (bio?.bats && bio?.throws) {
    parts.push(`${bio.bats}/${bio.throws}`);
  }

  const debutYear = extractYear(bio?.firstGame);
  const lastYear = extractYear(bio?.lastGame);
  if (debutYear && lastYear) {
    parts.push(debutYear === lastYear ? debutYear : `${debutYear}–${lastYear}`);
  } else if (career?.years) {
    parts.push(`${career.years} seasons`);
  }

  return parts.length > 0 ? parts.join(" · ") : null;
};
